(() => {
  'use strict';
  if (window.MuriclynMotion) return;
  const locks = new Set();
  const states = new WeakMap();
  let previousOverflow = '';
  let previousGutter = '';
  function lock(owner) {
    if (locks.has(owner)) return;
    if (!locks.size) {
      previousOverflow = document.documentElement.style.overflow;
      previousGutter = document.documentElement.style.scrollbarGutter;
      document.documentElement.style.scrollbarGutter = 'stable';
      document.documentElement.style.overflow = 'hidden';
    }
    locks.add(owner);
  }
  function unlock(owner) {
    if (!locks.delete(owner) || locks.size) return;
    document.documentElement.style.overflow = previousOverflow;
    document.documentElement.style.scrollbarGutter = previousGutter;
  }
  function settled(element) {
    const animations = element.getAnimations().filter(animation => animation.effect?.getTiming().iterations !== Infinity);
    return Promise.allSettled(animations.map(animation => animation.finished));
  }
  function stateFor(dialog) {
    if (states.has(dialog)) return states.get(dialog);
    const state = { revision: 0, trigger: null, restoreFocus: true };
    states.set(dialog, state);
    dialog.addEventListener('close', () => {
      if (dialog.open) return;
      ++state.revision;
      delete dialog.dataset.mrcMotion;
      unlock(dialog);
      if (state.restoreFocus && !document.querySelector('dialog[open], .mrc-menu-open') && state.trigger?.isConnected) {
        state.trigger.focus({ preventScroll: true });
      }
    });
    return state;
  }
  function open(dialog, trigger = document.activeElement) {
    const state = stateFor(dialog);
    ++state.revision;
    state.restoreFocus = true;
    if (!dialog.open) {
      state.trigger = trigger;
      dialog.dataset.mrcMotion = 'closed';
      lock(dialog);
      try { dialog.showModal(); } catch (error) { unlock(dialog); throw error; }
      dialog.getBoundingClientRect();
    }
    dialog.dataset.mrcMotion = 'open';
  }
  function close(dialog, { immediate = false, restoreFocus = true } = {}) {
    if (!dialog) return Promise.resolve();
    const state = stateFor(dialog);
    const revision = ++state.revision;
    state.restoreFocus = restoreFocus;
    if (!dialog.open) {
      unlock(dialog);
      return Promise.resolve();
    }
    dialog.dataset.mrcMotion = 'closing';
    const finish = () => {
      if (state.revision !== revision || !dialog.open) return;
      dialog.close();
      unlock(dialog);
    };
    if (immediate) {
      finish();
      return Promise.resolve();
    }
    return settled(dialog).then(finish);
  }
  window.MuriclynMotion = { open, close, lock, unlock, settled };
})();

(() => {
  'use strict';
  if (window.muriclynCartReady) return;
  const config = document.getElementById('MrcCartWords');
  if (!config) return;
  window.muriclynCartReady = true;
  const words = JSON.parse(config.textContent);
  const root = window.Shopify?.routes?.root || '/';
  let pending = false;
  let revision = 0;
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(`muriclyn:cart:${name}`, { detail }));
  const style = document.createElement('style');
  style.id = 'muriclyn-cart-style';
  style.textContent = `
    [data-mrc-state]{position:relative!important;transition:filter .18s ease}
    [data-mrc-state]>:not(.mrc-button-feedback){visibility:hidden!important}
    [data-mrc-state="loading"]{cursor:wait!important}
    .mrc-button-feedback{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;gap:10px;padding:8px;font:inherit;pointer-events:none}
    [data-mrc-state="loading"] .mrc-button-feedback::before{content:"";width:16px;height:16px;flex-shrink:0;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;animation:mrcSpin .7s linear infinite}
    [data-mrc-cart-status]{margin-top:12px;font:500 14px/1.5 system-ui;color:inherit}
    [data-mrc-cart-status] a{display:block;color:inherit;text-decoration:underline;padding-block:6px}
    [data-mrc-cart-status][hidden],[data-cart-count][hidden]{display:none!important}
    .mrc-bump{animation:mrcBump .35s ease}
    @keyframes mrcSpin{to{transform:rotate(360deg)}}
    @keyframes mrcBump{50%{transform:scale(1.12)}}
    @media(prefers-reduced-motion:reduce){.mrc-bump,[data-mrc-state="loading"] .mrc-button-feedback::before{animation:none}[data-mrc-state]{transition:none}}
  `;
  document.head.append(style);
  async function refresh(animate = false) {
    const current = ++revision;
    try {
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!response.ok) throw new Error();
      const cart = await response.json();
      if (current !== revision) return null;
      document.querySelectorAll('[data-cart-count], .header__cart-count').forEach(el => {
        el.textContent = cart.item_count > 99 ? '99+' : String(cart.item_count);
        el.hidden = !cart.item_count;
      });
      if (animate) document.querySelectorAll('.mrc-cart-link, .header__cart, [data-cart-toggle]').forEach(el => {
        el.classList.add('mrc-bump');
        el.addEventListener('animationend', () => el.classList.remove('mrc-bump'), { once: true });
      });
      emit('updated', cart);
      return cart;
    } catch {
      if (current === revision) emit('refresh-error');
      return null;
    }
  }
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form[action*="/cart/add"]')) return;
    const button = form.querySelector('[name="add"]');
    if (!button || (event.submitter && event.submitter !== button)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (pending || button.disabled) return;
    pending = true;
    ++revision;
    form.dataset.mrcPending = 'true';
    clearTimeout(button._mrcTimer);
    button._mrcReset?.();
    const originalAria = button.getAttribute('aria-label');
    const originalDisabled = button.getAttribute('aria-disabled');
    for (const node of [...button.childNodes]) {
      if (node.nodeType !== Node.TEXT_NODE || !node.textContent.trim()) continue;
      const span = document.createElement('span');
      node.replaceWith(span);
      span.append(node);
    }
    const overlay = document.createElement('span');
    overlay.className = 'mrc-button-feedback';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.textContent = words.loading;
    button.append(overlay);
    button.dataset.mrcState = 'loading';
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-disabled', 'true');
    button.setAttribute('aria-label', words.loading);
    let status = form.querySelector('[data-mrc-cart-status]');
    if (!status) {
      status = document.createElement('div');
      status.dataset.mrcCartStatus = '';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      form.append(status);
    }
    status.hidden = true;
    status.replaceChildren();
    emit('adding');
    const reset = () => {
      overlay.remove();
      delete button.dataset.mrcState;
      if (originalAria === null) button.removeAttribute('aria-label');
      else button.setAttribute('aria-label', originalAria);
      delete button._mrcReset;
    };
    button._mrcReset = reset;
    const cartLink = () => {
      const link = document.createElement('a');
      link.href = `${root}cart`;
      link.textContent = words.view;
      status.append(link);
    };
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST', body: new FormData(form), credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      });
      const result = await response.json();
      if (!response.ok) {
        status.textContent = typeof result.description === 'string' ? result.description : words.error;
        status.hidden = false;
        reset();
        return;
      }
      overlay.textContent = words.added;
      button.dataset.mrcState = 'added';
      button.setAttribute('aria-label', words.added);
      button.removeAttribute('aria-busy');
      emit('added', { trigger: button });
      const cart = await refresh(true);
      if (!cart || !document.getElementById('MrcCartPanel')?.open) {
        status.textContent = cart ? words.success : words.syncError;
        cartLink();
        status.hidden = false;
      }
      button._mrcTimer = setTimeout(reset, 1800);
    } catch {
      reset();
      status.textContent = words.networkError;
      cartLink();
      status.hidden = false;
    } finally {
      pending = false;
      delete form.dataset.mrcPending;
      button.removeAttribute('aria-busy');
      if (originalDisabled === null) button.removeAttribute('aria-disabled');
      else button.setAttribute('aria-disabled', originalDisabled);
      emit('settled');
    }
  }, true);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => refresh(), { once: true });
  else refresh();
  window.addEventListener('pageshow', event => { if (event.persisted) refresh(); });
  window.muriclynCartRefresh = refresh;
})();
